import { combineReducers, compose } from '@reduxjs/toolkit';

import slice from './text_nodes.slice';
import textNodesAdapter from './text_nodes.adapter';

import { selectDefaultPPI } from 'selectors/ppi';
import activeEditor from 'domains/active_editor';

import utils from './text_nodes.utils';

import type { RootState } from 'store/wos_store';
import type { CartProductTextNodeCreateParams } from 'domains/cart_products';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const defaultSelectors = textNodesAdapter.getSelectors();

const selectActiveTextNode = (state: LocalState) => state.activeNodeUid;

const selectTextNodesForCreateCartProduct = (
  state: RootState
): CartProductTextNodeCreateParams[] => {
  const product = activeEditor.selectors.product(state);
  if (!product) throw new Error('No active product');

  return product.nodes.filter(utils.isTextNode).map((node) => {
    const tn = defaultSelectors.selectById(localState(state), node.uid);
    if (!tn) throw new Error('No text node for this ID');

    const ppi = selectDefaultPPI(state);

    const svgNodeId = `${utils.textNodeSVGBaseId}${node.uid}`;
    let svg = document.getElementById(svgNodeId);

    if (!svg) throw new Error('No Text Node Rendered');

    // Avoid saving the default "Text Here" string as an acual text content string
    const textContent = tn.textContent === utils.textNodeDefaults.textContent ? '' : tn.textContent;

    svg = svg.cloneNode(true) as HTMLElement;
    const textElements = svg.querySelectorAll('text');
    textElements.forEach((textElement) => {
      if (textElement.innerHTML === utils.textNodeDefaults.textContent) {
        textElement.innerHTML = '';
      }
    });

    return {
      node_id: node.id,
      font_size: tn.fontSize,
      font_family: tn.fontFamily,
      text_color: tn.textColor,
      text_horizontal_alignment: tn.horizontalAlignment,
      text_vertical_alignment: tn.verticalAlignment,
      text_content: textContent,
      auto_fit: tn.autofit,
      ppi,
      svg: svg.outerHTML,
    };
  });
};

export default {
  ...textNodesAdapter.getSelectors(localState),
  activeNode: compose(selectActiveTextNode, localState),
  forCreateCartProduct: selectTextNodesForCreateCartProduct,
};
